/**
 * Mission 1 - Destroy a Munitions Depot (01 §3, 03 §2): the hand-built vertical-slice map.
 * Desert, day, threat 2. Destroy the ammo shack (lootable first), the explosives magazine
 * (LAW or bomb only) and the fuel dump, then recall the Osprey.
 *
 * 80 x 140 m. South edge is z = 140; the depot sits in the north. Two ways in: up the road
 * through the gate under the MG nest, or along the western ravine to a gap in the wire.
 */

import { PI } from '../math';
import { Pcg32 } from '../rng';
import type { MissionDef, PropDef, PropKind, SpawnDef } from '../types';

const NORTH = PI;
const SOUTH = 0;
const EAST = PI / 2;
const WEST = -PI / 2;

export function buildMunitionsDepot(seed = 1987): MissionDef {
  const rng = new Pcg32(seed, 11);
  const props: PropDef[] = [];
  let nextId = 1;
  const add = (kind: PropKind, x: number, z: number, extra: Partial<PropDef> = {}): number => {
    const id = nextId++;
    props.push({ id, kind, x, z, yaw: 0, ...extra });
    return id;
  };

  /* ---- the depot (goal zone), north */
  const targets = [
    add('ammoShack', 30, 30, { target: true }),
    add('magazine', 47, 21, { target: true }),
    add('fuelTank', 61, 31, { target: true }),
  ];
  add('hut', 21, 20);
  add('tent', 53, 40);
  const bunker = add('bunker', 40, 37);
  add('alarmPost', 36, 44);
  add('wall', 40, 10, { length: 56 });
  add('wall', 12.5, 24, { length: 0.7 });
  for (const [x, z] of [[58, 27], [58.9, 26.4], [63.5, 27.2], [65, 34], [26.5, 33.5]] as const) add('barrel', x, z);
  for (const [x, z] of [[33.5, 32], [34.2, 30.8], [26, 27], [44, 26], [50.5, 24.5]] as const) add('crate', x, z);

  /* ---- defensive belt: wire along z = 56 with the road gate and one quiet gap by the ravine */
  for (let x = 14; x <= 70; x += 4) {
    if (x === 42 || x === 46 || x === 26) continue;
    add('wire', x, 56, { length: 4 });
  }
  const nest = add('mgNest', 51, 52);
  add('watchtower', 31, 51);
  add('sandbags', 40.5, 58.5, { length: 3.6 });

  /* ---- approach: an outpost on the road, cover, an oasis */
  add('sandbags', 58, 92, { length: 4.4 });
  add('sandbags', 66, 96, { length: 3.6 });
  add('tent', 69, 88);
  add('crate', 60, 89.5);
  for (const [x, z] of [[9, 80], [14.5, 77.5], [16, 88], [7, 89], [11, 92]] as const) add('palm', x, z, { yaw: rng.range(0, 6.28) });

  /* ---- scatter: rocks, cactus and scrub, kept off the road, the ravine and the buildings */
  const keepOut = (x: number, z: number): boolean => {
    if (Math.abs(x - 62) < 5 && z > 58) return true; // road
    if (z < 60 && z > 8 && x > 10 && x < 74) return true; // depot and belt
    if (Math.abs(x - roadlessRavineX(z)) < 3.2 && z > 60 && z < 112) return true; // ravine
    if (Math.abs(z - 118) < 3 && x > 8 && x < 52) return true; // trench
    if ((x - 12) * (x - 12) + (z - 84) * (z - 84) < 9 * 9) return true; // oasis
    if ((x - 40) * (x - 40) + (z - 132) * (z - 132) < 6 * 6) return true; // landing spot
    for (const p of props) if ((p.x - x) * (p.x - x) + (p.z - z) * (p.z - z) < 3.2 * 3.2) return true;
    return false;
  };
  const scatter = (kind: PropKind, count: number, scaleMin: number, scaleMax: number): void => {
    let placed = 0;
    for (let tries = 0; tries < count * 12 && placed < count; tries++) {
      const x = rng.range(3, 77);
      const z = rng.range(60, 137);
      if (keepOut(x, z)) continue;
      add(kind, x, z, { yaw: rng.range(0, 6.28), scale: rng.range(scaleMin, scaleMax) });
      placed++;
    }
  };
  scatter('rock', 34, 0.8, 1.5);
  scatter('cactus', 26, 0.85, 1.2);
  scatter('shrub', 18, 0.9, 1.2);
  scatter('deadTree', 5, 0.9, 1.1);

  /* ---- the garrison */
  const spawns: SpawnDef[] = [
    // Road patrol: up and down between the drop band and the gate.
    { kind: 'rifleman', x: 62, z: 116, facing: NORTH, patrol: [[62, 122], [62, 70], [50, 60], [62, 70]] },
    { kind: 'rifleman', x: 63.4, z: 118, facing: NORTH, patrol: [[63.4, 124], [63.4, 72], [51, 62], [63.4, 72]] },
    // Outpost on the road.
    { kind: 'rifleman', x: 58, z: 93.6, facing: SOUTH },
    { kind: 'rifleman', x: 66.5, z: 97.4, facing: SOUTH },
    // The belt.
    { kind: 'rifleman', x: 51, z: 52.4, facing: SOUTH, mans: nest },
    { kind: 'rifleman', x: 44, z: 58.5, facing: SOUTH },
    { kind: 'rifleman', x: 24, z: 53, facing: WEST, patrol: [[18, 53], [36, 53], [36, 48], [18, 48]] },
    // Inside the wire.
    { kind: 'rifleman', x: 40, z: 38.2, facing: SOUTH, mans: bunker },
    { kind: 'rifleman', x: 30, z: 34, facing: SOUTH },
    { kind: 'elite', x: 47, z: 26.5, facing: SOUTH },
    { kind: 'rifleman', x: 61, z: 36, facing: SOUTH },
    { kind: 'officer', x: 53, z: 43.5, facing: SOUTH },
    { kind: 'rifleman', x: 66, z: 22, facing: EAST, patrol: [[68, 16], [68, 42], [16, 42], [16, 16]] },
    { kind: 'rifleman', x: 17, z: 30, facing: EAST, patrol: [[16, 42], [16, 16], [68, 16], [68, 42]] },
  ];

  return {
    name: 'DESTROY A MUNITIONS DEPOT',
    archetype: 'munitions-depot',
    seed,
    biome: 'desert',
    timeOfDay: 'day',
    weather: 'clear',
    sizeX: 80,
    sizeZ: 140,
    clockSeconds: 600,
    threat: 2,
    startAlert: 0,
    modifiers: [],
    intel: [],
    waterBarriers: [],
    mineFields: [],
    captives: [],
    objective: { kind: 'destroyTargets', label: 'DESTROY DEPOT', targetPropIds: targets, minFraction: 1, rules: [{ type: 'recall', mode: 'normal' }] },
    dropBand: { z0: 104, z1: 134 },
    wind: { x: 0.4, z: 0.1 },
    trenches: [
      { points: [[8, 118], [22, 117], [38, 119.5], [52, 118]], width: 2.2, depth: 1.25 },
      // The western ravine: the quiet road north, ending by the gap in the wire at x = 26.
      { points: [[20, 111], [23, 100], [21, 88], [25, 76], [26, 62]], width: 2.6, depth: 1.35 },
    ],
    roads: [{ points: [[62, 140], [62, 66], [52, 60], [44, 56], [44, 44]], width: 4.2 }],
    ponds: [{ x: 12, z: 84, r: 5.5 }],
    props,
    spawns,
    player: { x: 40, z: 132, facing: NORTH },
    pickup: { x: 69, z: 47 },
    goalZone: { x0: 12, z0: 8, x1: 74, z1: 56 },
  };
}

/** Centre line of the western ravine at a given z, for keeping scatter out of it. */
function roadlessRavineX(z: number): number {
  const pts: ReadonlyArray<readonly [number, number]> = [[20, 111], [23, 100], [21, 88], [25, 76], [26, 62]];
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    if (!a || !b) continue;
    if (z <= a[1] && z >= b[1]) return a[0] + ((b[0] - a[0]) * (a[1] - z)) / (a[1] - b[1]);
  }
  return 23;
}
